<script lang="ts" setup>
import { Employee } from '@/core/collections/contracts-api/employees/employee.interface';
import { CaseFile } from '@/core/collections/contracts-api/employers/case-files/case-file.interface';
import { NxtGlobalModalKey, ProgressionState, useGlobalModal } from '@nxtpeople/nxt-styles';

//#region Globals
const { openModal } = useGlobalModal();
//#endregion

//#region Props & Emits
defineProps<{
   progressionState: ProgressionState;
   caseFiles: Array<CaseFile>;
}>();

const emit = defineEmits<{
   caseFileClicked: [caseFile: CaseFile];
}>();
//#endregion

//#region Collapse State
const isCollapsed = ref(false);

function toggleCollapse() {
   isCollapsed.value = !isCollapsed.value;
}
//#endregion

//#region Candidate
function openCandidate(caseFile: CaseFile) {
   if (!(caseFile.employee as Employee)?.candidate) return;

   openModal(NxtGlobalModalKey.CANDIATE_DETAILS, {
      candidateSyncId: (caseFile.employee as Employee).candidate?.sync_id,
      initialTab: 'case-files',
   });
}
//#endregion
</script>

<template>
   <div :class="'follow-up-progression-state'">
      <div :class="['follow-up-progression-state__header', { isCollapsed }]" @click="toggleCollapse">
         <NxtIcon :name="'chevron-down'" />
         <h3>{{ progressionState.name }}</h3>
      </div>

      <Transition name="fade">
         <div v-if="!isCollapsed" class="follow-up-progression-state__content">
            <NxtTable :count="caseFiles.length" :hover-effect="true">
               <template #head>
                  <th :style="{ minWidth: '20rem' }">{{ $t('case-files.case-file-type') }}</th>
                  <th :style="{ minWidth: '20rem' }">{{ $t('nxt-candidates.candidate') }}</th>
                  <th :style="{ minWidth: '20rem' }">{{ $t('customers.customer') }}</th>
                  <th :style="{ minWidth: '10rem' }">{{ $t('generic.description') }}</th>
                  <th :style="{ minWidth: '10rem' }">{{ $t('generic.dates.from') }}</th>
                  <th :style="{ minWidth: '10rem' }">{{ $t('generic.dates.till') }}</th>
                  <th :style="{ width: '100%' }">{{ $t('generic.team') }}</th>
               </template>

               <template #body>
                  <tr v-for="caseFile in caseFiles" :key="caseFile.id" @click="$emit('caseFileClicked', caseFile)">
                     <td>
                        {{ caseFile.case_file_type?.name }}
                     </td>
                     <td>
                        <NxtLink @click.stop class="d-flex align-items-center gap-2" @on-click="openCandidate(caseFile)">
                           <NxtProfilePicture
                              :profile_picture="caseFile.employee?.candidate?.profile_picture_url"
                              :size-in-px="28"
                              :username="`${caseFile.employee?.candidate?.last_name} ${caseFile.employee?.candidate?.first_name}`"
                           />

                           <strong v-if="caseFile.employee?.candidate">
                              <NxtFullname
                                 :first-name="caseFile.employee.candidate.first_name"
                                 :last-name="caseFile.employee.candidate.last_name"
                              />
                           </strong>
                        </NxtLink>
                     </td>
                     <td>
                        <RouterLink v-if="caseFile.customer" :to="`/customers/${caseFile.customer.id}`">
                           {{ caseFile.customer.name }}
                        </RouterLink>
                        <span v-else>-</span>
                     </td>
                     <td>
                        {{ caseFile.description ?? '-' }}
                     </td>
                     <td v-date="caseFile.from"></td>
                     <td v-date="caseFile.till"></td>
                     <td>{{ caseFile.created_by_team?.name }}</td>
                  </tr>
               </template>

               <template #noResults>
                  <span>{{ $t('generic.feedback.no-results-for-type', { type: $t('case-files.case-file', 2).toLowerCase() }) }}</span>
               </template>
            </NxtTable>
         </div>
      </Transition>
   </div>
</template>

<style lang="scss" scoped>
.follow-up-progression-state {
   width: 100%;
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter-large);
   height: fit-content;
}

.follow-up-progression-state__header {
   display: flex;
   align-items: center;
   gap: var(--nxt-gutter);
   position: sticky;
   top: 0;
   background-color: var(--surface-base-0);
   z-index: var(--zindex-sticky);
   padding: toRem(10) 0;

   cursor: pointer;
   width: 100%;

   &:deep(.nxt-icon) {
      transition: transform 0.2s ease-in-out;
   }

   h3 {
      margin: 0;
   }

   &.isCollapsed {
      &:deep(.nxt-icon) {
         transform: rotate(-90deg);
      }
   }
}
</style>
